import { subject } from '@casl/ability';
import {
    getItemMap,
    hasCustomBinDimension,
    type ApiExploreResults,
    type EChartsSeries,
} from '@lightdash/common';
import { Menu } from '@mantine-8/core';
import { Portal } from '@mantine/core';
import { useClipboard } from '@mantine/hooks';
import { IconCopy, IconStack } from '@tabler/icons-react';
import {
    memo,
    useCallback,
    useEffect,
    useMemo,
    useState,
    type FC,
} from 'react';
import useToaster from '../../../hooks/toaster/useToaster';
import { useProjectUuid } from '../../../hooks/useProjectUuid';
import { Can } from '../../../providers/Ability';
import useApp from '../../../providers/App/useApp';
import useTracking from '../../../providers/Tracking/useTracking';
import { EventName } from '../../../types/Events';
import { useVisualizationContext } from '../../LightdashVisualization/useVisualizationContext';
import DrillDownMenuItem from '../../MetricQueryData/DrillDownMenuItem';
import { useMetricQueryDataContext } from '../../MetricQueryData/useMetricQueryDataContext';
import { getDataFromChartClick } from '../../MetricQueryData/utils';
import { type EchartsSeriesClickEvent } from '../../SimpleChart';
import MantineIcon from '../../common/MantineIcon';

export const SeriesContextMenu: FC<{
    echartsSeriesClickEvent: EchartsSeriesClickEvent | undefined;
    dimensions: string[] | undefined;
    series: EChartsSeries[] | undefined;
    explore: ApiExploreResults | undefined;
}> = memo(({ echartsSeriesClickEvent, dimensions, series, explore }) => {
    const { showToastSuccess } = useToaster();
    const clipboard = useClipboard({ timeout: 200 });
    const { track } = useTracking();
    const { user } = useApp();

    const context = useVisualizationContext();
    const { resultsData: { metricQuery } = {} } = context;

    const [contextMenuIsOpen, setContextMenuIsOpen] = useState(false);
    const { openUnderlyingDataModal } = useMetricQueryDataContext();

    const [contextMenuTargetOffset, setContextMenuTargetOffset] = useState<{
        left: number;
        top: number;
    }>();

    const projectUuid = useProjectUuid();

    useEffect(() => {
        if (echartsSeriesClickEvent !== undefined) {
            const e: EchartsSeriesClickEvent = echartsSeriesClickEvent;

            setContextMenuIsOpen(true);
            setContextMenuTargetOffset({
                left: e.event.event.pageX,
                top: e.event.event.pageY,
            });
        }
    }, [echartsSeriesClickEvent]);

    const underlyingData = useMemo(() => {
        if (explore !== undefined && echartsSeriesClickEvent !== undefined) {
            const allItemsMap = getItemMap(
                explore,
                metricQuery?.additionalMetrics,
                metricQuery?.tableCalculations,
            );

            return getDataFromChartClick(
                echartsSeriesClickEvent,
                allItemsMap,
                series || [],
            );
        }
    }, [echartsSeriesClickEvent, explore, metricQuery, series]);

    const handleCopyToClipboard = useCallback(() => {
        if (underlyingData === undefined) return;
        const value = underlyingData.value.formatted;

        clipboard.copy(value);
        showToastSuccess({ title: 'Copied to clipboard!' });
    }, [underlyingData, clipboard, showToastSuccess]);

    const handleViewUnderlyingData = useCallback(() => {
        if (underlyingData === undefined) return;

        track({
            name: EventName.VIEW_UNDERLYING_DATA_CLICKED,
            properties: {
                organizationId: user?.data?.organizationUuid,
                userId: user?.data?.userUuid,
                projectId: projectUuid,
            },
        });

        openUnderlyingDataModal({
            ...underlyingData,
            dimensions,
        });
    }, [
        underlyingData,
        dimensions,
        openUnderlyingDataModal,
        track,
        user?.data?.organizationUuid,
        user?.data?.userUuid,
        projectUuid,
    ]);

    const handleCancelContextMenu = useCallback(
        (e: React.SyntheticEvent<HTMLDivElement>) => e.preventDefault(),
        [],
    );

    const onClose = useCallback(() => setContextMenuIsOpen(false), []);

    return (
        <Menu
            opened={contextMenuIsOpen}
            onClose={onClose}
            withinPortal
            closeOnItemClick
            closeOnEscape
            shadow="md"
            radius={0}
            position="right-start"
            offset={{
                mainAxis: 0,
                crossAxis: 0,
            }}
        >
            <Portal>
                <Menu.Target>
                    <div
                        onContextMenu={handleCancelContextMenu}
                        style={{
                            position: 'absolute',
                            ...contextMenuTargetOffset,
                        }}
                    />
                </Menu.Target>
            </Portal>

            <Menu.Dropdown>
                {underlyingData?.value && (
                    <Menu.Item
                        leftSection={<MantineIcon icon={IconCopy} />}
                        onClick={handleCopyToClipboard}
                    >
                        Copy value
                    </Menu.Item>
                )}

                <Can
                    I="view"
                    this={subject('UnderlyingData', {
                        organizationUuid: user.data?.organizationUuid,
                        projectUuid: projectUuid,
                    })}
                >
                    {!hasCustomBinDimension(metricQuery) && (
                        <Menu.Item
                            leftSection={<MantineIcon icon={IconStack} />}
                            onClick={handleViewUnderlyingData}
                        >
                            View underlying data
                        </Menu.Item>
                    )}
                </Can>

                <Can
                    I="view"
                    this={subject('Explore', {
                        organizationUuid: user.data?.organizationUuid,
                        projectUuid: projectUuid,
                    })}
                >
                    <DrillDownMenuItem
                        {...underlyingData}
                        trackingData={{
                            organizationId: user?.data?.organizationUuid,
                            userId: user?.data?.userUuid,
                            projectId: projectUuid,
                        }}
                    />
                </Can>
            </Menu.Dropdown>
        </Menu>
    );
});
